import React, { FC } from 'react';
import { ControllerRenderProps } from 'react-hook-form';

import { InternationalTransferRequirementsFieldType } from '__generated__/GQL';
import * as Form from 'components/_core/form';

import { InternationalTransferDynamicFieldConfig } from '../types';

interface InternationalTransferDynamicInputProps
  extends Pick<ControllerRenderProps, 'name' | 'onChange' | 'onBlur' | 'value'>,
    Pick<InternationalTransferDynamicFieldConfig, 'type' | 'valuesAllowed'> {
  isInvalid: boolean;
}

const unhandledInputType = (type: never): never => {
  throw new Error(`Unhandled input type: ${type}`);
};

const MAX_SELECT_OPTIONS_WITHOUT_SEARCH = 5;

export const getInputTestId = (
  type: InternationalTransferRequirementsFieldType,
  name: string,
  value?: string | number,
) => {
  const testId = `InternationalTransferDynamicInput-${type}-${name}`;
  return value ? `${testId}-${value}` : testId;
};

export const InternationalTransferDynamicInput: FC<
  InternationalTransferDynamicInputProps
> = ({ isInvalid, name, onBlur, onChange, type, value, valuesAllowed }) => {
  switch (type) {
    case InternationalTransferRequirementsFieldType.Text:
      return (
        <Form.Input
          data-testid={getInputTestId(type, name)}
          onBlur={onBlur}
          onChange={onChange}
          type="text"
          value={value}
        />
      );

    case InternationalTransferRequirementsFieldType.Radio:
      if (!valuesAllowed) {
        throw new Error(`valuesAllowed is required for input of type ${type}`);
      }

      return (
        <Form.Radio.Group direction="row" radioPosition="right" value={value}>
          {valuesAllowed.map(({ key, name: label }) => (
            <Form.Radio
              data-testid={getInputTestId(type, name, key)}
              isInvalid={isInvalid}
              key={key}
              onBlur={onBlur}
              onChange={onChange}
              value={key}
              variant="outline"
            >
              <Form.Radio.Title>{label}</Form.Radio.Title>
            </Form.Radio>
          ))}
        </Form.Radio.Group>
      );

    case InternationalTransferRequirementsFieldType.Select:
      if (!valuesAllowed) {
        throw new Error(`valuesAllowed is required for input of type ${type}`);
      }

      return (
        <div data-testid={getInputTestId(type, name)}>
          <Form.SunshineSelect
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.key}
            isInvalid={isInvalid}
            isSearchable={
              valuesAllowed.length > MAX_SELECT_OPTIONS_WITHOUT_SEARCH
            }
            onBlur={onBlur}
            onChange={(selectedOption) => onChange(selectedOption?.key)}
            options={valuesAllowed}
            value={valuesAllowed.find((option) => option.key === value)}
          />
        </div>
      );

    case InternationalTransferRequirementsFieldType.Date:
      return (
        <Form.Input
          data-testid={getInputTestId(type, name)}
          onChange={(event) => {
            // native date input format is already yyyy-mm-dd.
            onChange(event.target.value);
          }}
          type="date"
          value={value}
        />
      );

    default:
      return unhandledInputType(type);
  }
};
